/* File: calling.jsx
 * Path: ananunadm/src/components/chat/calling.jsx
 *
 * 1-on-1 audio/video calling via PeerJS with Socket.IO signaling.
 * Video fix: callback refs guarantee srcObject is assigned every time a video
 * element mounts, regardless of modal transition timing.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
	Button, Modal, rem, Stack, Text, Title, Group, Avatar,
	Flex, ActionIcon, Tooltip, Badge, Paper, Box, Loader, Switch, Popover,
} from "@mantine/core";
import {
	IconMicrophone, IconMicrophoneOff, IconPhone, IconPhoneOff,
	IconVideo, IconVideoOff, IconScreenShare, IconScreenShareOff,
	IconMinimize, IconArrowsMaximize, IconArrowsMinimize, IconCameraRotate,
} from "@tabler/icons-react";
import Peer from "peerjs";
import { createBackgroundBlur } from "../../helpers/callBackgroundBlur";
import config from "../../config";
import * as API from "../../helpers/api";
import { getUserImageSrc } from "../../helpers/uploadImage";

const formatCallDuration = (durationSeconds = 0) => {
	const safeSeconds = Math.max(0, Math.floor(Number(durationSeconds) || 0));
	const hours = Math.floor(safeSeconds / 3600);
	const minutes = Math.floor((safeSeconds % 3600) / 60);
	const seconds = safeSeconds % 60;
	if (hours > 0) {
		return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
	}
	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

/** When intrinsic size is unknown, assume portrait (typical for phone front camera). */
const DEFAULT_LOCAL_VIDEO_ASPECT = 9 / 16;

/** Do not lock 16:9 — lets devices use native camera framing (e.g. 9:16 on mobile). */
const getCameraVideoConstraints = () => ({ facingMode: "user" });

const ENHANCEMENTS_KEY = "apit_call_enhancements";
const DEFAULT_AUDIO_EFFECTS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

/** Mirror + mic processing choices survive between calls (per browser). */
const loadEnhancements = () => {
	try {
		const saved = JSON.parse(window.localStorage.getItem(ENHANCEMENTS_KEY) || "{}");
		return {
			mirrorSelf: typeof saved.mirrorSelf === "boolean" ? saved.mirrorSelf : true,
			audioEffects: { ...DEFAULT_AUDIO_EFFECTS, ...(saved.audioEffects || {}) },
		};
	} catch (_) {
		return { mirrorSelf: true, audioEffects: DEFAULT_AUDIO_EFFECTS };
	}
};

const getLocalizedValue = (value, lang) => {
	if (typeof value === "string") return value.trim();
	if (!value || typeof value !== "object") return "";
	return value?.[lang] || value?.en || value?.ru || value?.hy ||
		Object.values(value).find((item) => typeof item === "string" && item.trim()) || "";
};

const getCallPartnerName = (partner, lang, fallback = "User") => {
	const fullName = getLocalizedValue(partner?.fullName, lang);
	if (fullName) return fullName;
	const name = getLocalizedValue(partner?.name, lang);
	if (name) return name;
	const firstName = getLocalizedValue(partner?.firstName, lang);
	const lastName = getLocalizedValue(partner?.lastName, lang);
	return [firstName, lastName].filter(Boolean).join(" ") || partner?.username || fallback;
};

const getCallPartnerImage = (partner, targetWidth) =>
	getUserImageSrc(partner, { targetWidth });

const serializeSessionDescription = (description) => {
	if (!description) return null;
	const json = typeof description.toJSON === "function" ? description.toJSON() : description;
	const type = json?.type ?? description?.type ?? json?._type ?? description?._type;
	const sdp = json?.sdp ?? description?.sdp ?? json?._sdp ?? description?._sdp;
	if (!["offer", "answer"].includes(type) || typeof sdp !== "string" || !sdp) return null;
	return { type, sdp };
};

const serializeIceCandidate = (candidate) => {
	if (!candidate) return null;
	const json = typeof candidate.toJSON === "function" ? candidate.toJSON() : candidate;
	const value = json?.candidate ?? candidate?.candidate;
	if (typeof value !== "string" || !value) return null;
	return {
		candidate: value,
		sdpMid: json?.sdpMid ?? candidate?.sdpMid ?? null,
		sdpMLineIndex: json?.sdpMLineIndex ?? candidate?.sdpMLineIndex ?? null,
		usernameFragment: json?.usernameFragment ?? candidate?.usernameFragment ?? null,
	};
};

export function Calling({
	partner,
	socket,
	user,
	lang,
	callType,
	setCallType,
	incomingCall,
	setIncomingCall,
	incomingCallType,
	setIncomingCallType,
	incomingCallId,
	setIncomingCallId,
	incomingCallSignal,
	setIncomingCallSignal,
	caller,
	chatId,
	setChatId,
	canViewIncomingAudioCall = true,
	canAnswerIncomingAudioCall = true,
	canViewIncomingVideoCall = true,
	canAnswerIncomingVideoCall = true,
}) {
	const [isDark, setIsDark] = useState(false);
	const audioRef = useRef(new Audio("/assets/call.mp3"));
	const [audioMuted, setAudioMuted] = useState(false);
	const [videoMuted, setVideoMuted] = useState(false);
	const [peerCameraOn, setPeerCameraOn] = useState(true);
	/** The peer's frames stopped (a phone stops capturing when its camera is turned off). */
	const [peerVideoStalled, setPeerVideoStalled] = useState(false);
	const [mirrorSelf, setMirrorSelf] = useState(() => loadEnhancements().mirrorSelf);
	const [backgroundBlur, setBackgroundBlur] = useState(false);
	const [enhancementBusy, setEnhancementBusy] = useState(false);
	const [audioEffects, setAudioEffects] = useState(() => loadEnhancements().audioEffects);
	const [facingMode, setFacingMode] = useState("user");
	const [canFlipCamera, setCanFlipCamera] = useState(false);
	const blurRef = useRef(null);
	const [isSharing, setIsSharing] = useState(false);
	/** The other side is sharing its screen (socket `callScreenShare`). */
	const [peerSharing, setPeerSharing] = useState(false);
	const [isMinimized, setIsMinimized] = useState(false);
	/** width / height of local camera frames — drives PiP box aspect */
	const [localPipAspect, setLocalPipAspect] = useState(DEFAULT_LOCAL_VIDEO_ASPECT);
	const [isCallVideoFullscreen, setIsCallVideoFullscreen] = useState(false);

	const [stream, setStream] = useState(null);
	const [calling, setCalling] = useState(false);
	const [callAccepted, setCallAccepted] = useState(false);
	const [callEnded, setCallEnded] = useState(false);

	const [elapsed, setElapsed] = useState(0);
	const timerRef = useRef(null);

	// Media stream refs — source of truth for srcObject assignments
	const streamRef = useRef(null);       // local camera/mic stream
	const remoteStreamRef = useRef(null); // remote peer's stream
	const screenStreamRef = useRef(null); // screen share stream
	const peerVideoTrackRef = useRef(null); // peer's video track (their screen, in audio calls)

	// DOM element refs — updated by callback refs below
	const localVideoElRef = useRef(null);
	const remoteVideoElRef = useRef(null);
	const remoteAudioElRef = useRef(null);
	const videoCallStageRef = useRef(null);
	const iOSVideoFullscreenEndCleanupRef = useRef(null);

	const peerRef = useRef(null);
	const currentCallRef = useRef(null);
	const directPeerConnectionRef = useRef(null);
	const pendingDirectIceCandidatesRef = useRef([]);
	const isDirectWebRTCCallRef = useRef(false);
    const restartIdRef = useRef(null);
    const restartTimerRef = useRef(null);
    const restartingRef = useRef(false);
	const callAcceptedListenerRef = useRef(null);
	const incomingPeerCallListenerRef = useRef(null);
	const callStartedAtRef = useRef(null);
	const isEndingCallRef = useRef(false);
	const callHistoryLoggedRef = useRef(false);
	const connectedCallTypeRef = useRef(null);
	const currentCallIdRef = useRef(null);
    const setupGenerationRef = useRef(0);

	// STUN + TURN from the API (same list as the mobile app); STUN-only until it loads.
	const directWebRTCConfigRef = useRef({
		iceServers: [
			{ urls: "stun:stun.l.google.com:19302" },
			{ urls: "stun:stun1.l.google.com:19302" },
		],
	});
	useEffect(() => {
		let cancelled = false;
		API.get({ route: "/chat/ice-servers", timeout: 4000 })
			.then((data) => {
				if (!cancelled && Array.isArray(data?.iceServers) && data.iceServers.length) {
					directWebRTCConfigRef.current = { iceServers: data.iceServers };
				}
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	const activeCallType = callType || incomingCallType;
	const canViewCurrentIncomingCall =
		incomingCallType === "video" ? canViewIncomingVideoCall : canViewIncomingAudioCall;
	const canAnswerCurrentIncomingCall =
		incomingCallType === "video" ? canAnswerIncomingVideoCall : canAnswerIncomingAudioCall;

	const modalStyles = {
		content: {
			backgroundColor: isDark ? "#111827" : "#ffffff",
			color: isDark ? "#e5e7eb" : "#1f2937",
			border: isDark ? "1px solid #1f2937" : "1px solid #e5e7eb",
		},
	};

	const callingModalStyles = {
		...modalStyles,
		body: {
			padding: "22px 24px 20px",
		},
	};

	// ─── Callback refs ────────────────────────────────────────────────────────────
	// These fire every time an element mounts, so srcObject is always assigned
	// even when modal transitions swap the underlying DOM node.

	const setLocalVideoRef = useCallback((el) => {
		localVideoElRef.current = el;
		if (el && streamRef.current) {
			el.srcObject = screenStreamRef.current || blurRef.current?.stream || streamRef.current;
		}
	}, []);

	const setRemoteVideoRef = useCallback((el) => {
		if (iOSVideoFullscreenEndCleanupRef.current) {
			iOSVideoFullscreenEndCleanupRef.current();
			iOSVideoFullscreenEndCleanupRef.current = null;
		}
		remoteVideoElRef.current = el;
		if (el && remoteStreamRef.current) {
			el.srcObject = remoteStreamRef.current;
			playRemoteMedia(el);
		}
		if (el) {
			const onEnd = () => setIsCallVideoFullscreen(false);
			el.addEventListener("webkitendfullscreen", onEnd);
			iOSVideoFullscreenEndCleanupRef.current = () =>
				el.removeEventListener("webkitendfullscreen", onEnd);
		}
	}, []);

	const playRemoteMedia = (el) => {
		if (!el) return;
		const playPromise = el.play?.();
		if (playPromise?.catch) {
			playPromise.catch((err) => {
				console.debug("Remote audio playback was deferred:", err);
			});
		}
	};

	/** Audio call: show the peer's shared screen from their video track alone. */
	const setPeerScreenRef = useCallback((el) => {
		if (!el) return;
		const track = peerVideoTrackRef.current;
		if (!track) return;
		if (el.srcObject?.getVideoTracks?.()[0] !== track) el.srcObject = new MediaStream([track]);
		playRemoteMedia(el);
	}, []);

	const setRemoteAudioRef = useCallback((el) => {
		remoteAudioElRef.current = el;
		if (el && remoteStreamRef.current) {
			el.srcObject = remoteStreamRef.current;
			playRemoteMedia(el);
		}
	}, []);

	// ─── Assign a new local stream to the local video element ────────────────────

	const assignLocalStream = (mediaStream) => {
		streamRef.current = mediaStream;
		if (localVideoElRef.current) localVideoElRef.current.srcObject = mediaStream;
	};

	// ─── Assign a new remote stream to the remote video element ─────────────────

	const assignRemoteStream = (remoteStream) => {
		remoteStreamRef.current = remoteStream;
		if (remoteVideoElRef.current) {
			remoteVideoElRef.current.srcObject = remoteStream;
			playRemoteMedia(remoteVideoElRef.current);
		}
		if (remoteAudioElRef.current) {
			remoteAudioElRef.current.srcObject = remoteStream;
			playRemoteMedia(remoteAudioElRef.current);
		}
	};

	const hasDirectRemoteDescription = () => {
		const pc = directPeerConnectionRef.current;
		return Boolean(pc?.remoteDescription || pc?.currentRemoteDescription || pc?.pendingRemoteDescription);
	};

	const flushDirectIceCandidates = async () => {
		const pc = directPeerConnectionRef.current;
		if (!pc || !hasDirectRemoteDescription() || pendingDirectIceCandidatesRef.current.length === 0) return;
		const candidates = pendingDirectIceCandidatesRef.current;
		pendingDirectIceCandidatesRef.current = [];
		for (const candidate of candidates) {
			try {
				await pc.addIceCandidate(new RTCIceCandidate(candidate));
			} catch (err) {
				console.warn("Failed to add queued ICE candidate:", err);
			}
		}
	};

	const addDirectIceCandidate = async (candidate) => {
		if (!candidate) return;
		const pc = directPeerConnectionRef.current;
		if (!pc || !hasDirectRemoteDescription() || (candidate.usernameFragment && !pc.remoteDescription?.sdp?.includes(`a=ice-ufrag:${candidate.usernameFragment}`))) {
			pendingDirectIceCandidatesRef.current.push(candidate);
            if (pendingDirectIceCandidatesRef.current.length > 100) pendingDirectIceCandidatesRef.current.shift();
			return;
		}
		try {
			await pc.addIceCandidate(new RTCIceCandidate(candidate));
		} catch (err) {
			console.warn("Failed to add ICE candidate:", err);
		}
	};

	const closeDirectPeerConnection = ({ clearCandidates = true } = {}) => {
		clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
        restartIdRef.current = null; restartingRef.current = false;
        const old = directPeerConnectionRef.current;
        directPeerConnectionRef.current = null;
        if (old) { old.ontrack = null; old.onicecandidate = null; old.onconnectionstatechange = null; old.close(); }
		if (clearCandidates) pendingDirectIceCandidatesRef.current = [];
		isDirectWebRTCCallRef.current = false;
	};

    const recoverDirectConnection = async () => {
        const pc = directPeerConnectionRef.current;
        const callId = currentCallIdRef.current;
        if (!pc || !callId || !pc.remoteDescription || pc.signalingState !== 'stable' || restartingRef.current) return;
        const peerId = partner?._id || caller;
        if (!peerId) return;
        restartingRef.current = true;
        try {
            if (pc.localDescription?.type !== 'offer') {
                socket.emit('callRecoveryRequest', { callId });
                return;
            }
            const restartId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            restartIdRef.current = restartId;
            const offer = await pc.createOffer({ iceRestart: true });
            if (directPeerConnectionRef.current !== pc) return;
            await pc.setLocalDescription(offer);
            if (directPeerConnectionRef.current !== pc) return;
            socket.emit('callRenegotiate', { callId, restartId, signal: serializeSessionDescription(pc.localDescription) });
        } catch (error) { console.warn('Call recovery failed:', error.message); }
        finally { if (directPeerConnectionRef.current === pc) restartingRef.current = false; }
    };

	const createDirectPeerConnection = (peerId, resolvedCallType) => {
		closeDirectPeerConnection({ clearCandidates: false });
		const pc = new RTCPeerConnection(directWebRTCConfigRef.current);
		directPeerConnectionRef.current = pc;
		isDirectWebRTCCallRef.current = true;

		pc.onicecandidate = (event) => {
			if (event.candidate && socket) {
				socket.emit("iceCandidate", {
					to: peerId,
					candidate: serializeIceCandidate(event.candidate),
					callId: currentCallIdRef.current,
				});
			}
		};

		pc.ontrack = (event) => {
            if (directPeerConnectionRef.current !== pc) return;
			// Audio calls carry an idle video channel for screen sharing; a phone that answered
			// sends that track without a stream, so keep the track itself.
			if (event.track?.kind === "video") peerVideoTrackRef.current = event.track;
			const [remoteStream] = event.streams || [];
			if (!remoteStream) return;
			assignRemoteStream(remoteStream);
			markCallConnected(resolvedCallType);
			setCalling(false);
			setCallAccepted(true);
		};

		pc.onconnectionstatechange = () => {
            if (directPeerConnectionRef.current !== pc) return;
			if (pc.connectionState === 'connected') {
                clearTimeout(restartTimerRef.current); restartTimerRef.current = null;
            } else if (['failed', 'disconnected'].includes(pc.connectionState)) {
                void recoverDirectConnection();
                if (!restartTimerRef.current) restartTimerRef.current = setTimeout(() => {
                    restartTimerRef.current = null;
                    if (directPeerConnectionRef.current === pc && pc.connectionState !== 'connected') endCall({ notifyRemote: true });
                }, 30000);
            } else if (pc.connectionState === 'closed') endCall({ notifyRemote: false });
		};

		streamRef.current?.getTracks?.().forEach((track) => {
			pc.addTrack(track, streamRef.current);
		});

		return pc;
	};

	const getVideoSender = useCallback(() => {
		const peerConnection = directPeerConnectionRef.current || currentCallRef.current?.peerConnection;
		if (!peerConnection?.getSenders) return null;
		const withTrack = peerConnection.getSenders().find((sender) => sender.track?.kind === "video");
		if (withTrack) return withTrack;
		// Audio call: the idle screen-share video channel (never the audio sender).
		const channel = peerConnection.getTransceivers?.().find((t) =>
			t.receiver?.track?.kind === "video" && !t.stopped && ["sendrecv", "sendonly"].includes(t.direction)
		);
		return channel?.sender || null;
	}, []);

	const replaceOutgoingVideoTrack = useCallback(async (videoTrack) => {
		const sender = getVideoSender();
		if (!sender) return false;
		await sender.replaceTrack(videoTrack || null);
		return true;
	}, [getVideoSender]);

	const emitScreenShareState = useCallback((sharing) => {
		const to = caller || partner?._id;
		if (!socket || !to) return;
		socket.emit("callScreenShare", { to, callId: currentCallIdRef.current, sharing });
	}, [socket, caller, partner?._id]);

	const updateLocalPipFromVideoElement = useCallback((el) => {
		if (!el) return;
		const w = el.videoWidth;
		const h = el.videoHeight;
		if (w > 0 && h > 0) setLocalPipAspect(w / h);
	}, []);

	/** `getSettings()` often reports negotiated camera size before frames decode */
	useEffect(() => {
		if (!stream) return;
		const vt = stream.getVideoTracks?.()[0];
		if (!vt) return;
		const s = vt.getSettings?.() || {};
		if (s.width && s.height) setLocalPipAspect(s.width / s.height);
	}, [stream]);

	useEffect(() => {
		try {
			window.localStorage.setItem(ENHANCEMENTS_KEY, JSON.stringify({ mirrorSelf, audioEffects }));
		} catch (_) {}
	}, [mirrorSelf, audioEffects]);

	useEffect(() => {
		if (!stream?.getVideoTracks?.().length) return;
		let cancelled = false;
		navigator.mediaDevices?.enumerateDevices?.()
			.then((devices) => {
				if (!cancelled) setCanFlipCamera(devices.filter((d) => d.kind === "videoinput").length > 1);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [stream]);

	// Fallback for the camera-off signal: no decoded frames for ~2s means the picture is frozen.
	useEffect(() => {
		if (!callAccepted || activeCallType !== "video") return;
		let lastFrames = null;
		let idleTicks = 0;
		const timer = setInterval(async () => {
			const pc = directPeerConnectionRef.current || currentCallRef.current?.peerConnection;
			const receiver = pc?.getReceivers?.().find((r) => r.track?.kind === "video");
			if (!receiver) return;
			try {
				let frames = 0;
				(await receiver.getStats()).forEach((report) => {
					if (report.type === "inbound-rtp" && (report.kind || report.mediaType) === "video") frames += report.framesDecoded || 0;
				});
				idleTicks = lastFrames !== null && frames === lastFrames ? idleTicks + 1 : 0;
				lastFrames = frames;
				setPeerVideoStalled(frames > 0 && idleTicks >= 2);
			} catch (_) {}
		}, 1000);
		return () => {
			clearInterval(timer);
			setPeerVideoStalled(false);
		};
	}, [callAccepted, activeCallType]);

	const toggleCallVideoFullscreen = useCallback(async () => {
		if (typeof document === "undefined") return;
		const stage = videoCallStageRef.current;
		const remoteV = remoteVideoElRef.current;
		if (document.fullscreenElement || document.webkitFullscreenElement) {
			try {
				if (document.exitFullscreen) await document.exitFullscreen();
				else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
			} catch (_) {}
			return;
		}
		try {
			if (stage?.requestFullscreen) {
				await stage.requestFullscreen();
			} else if (stage?.webkitRequestFullscreen) {
				stage.webkitRequestFullscreen();
			} else if (remoteV?.requestFullscreen) {
				await remoteV.requestFullscreen();
			} else if (remoteV?.webkitEnterFullscreen) {
				remoteV.webkitEnterFullscreen();
				setIsCallVideoFullscreen(true);
			}
		} catch (e) {
			console.debug("Fullscreen:", e);
		}
	}, []);

	useEffect(() => {
		if (typeof document === "undefined") return;
		const sync = () => {
			const fs = document.fullscreenElement ?? document.webkitFullscreenElement;
			setIsCallVideoFullscreen(!!fs);
		};
		document.addEventListener("fullscreenchange", sync);
		document.addEventListener("webkitfullscreenchange", sync);
		return () => {
			document.removeEventListener("fullscreenchange", sync);
			document.removeEventListener("webkitfullscreenchange", sync);
		};
	}, []);

	// ─── Dark mode ────────────────────────────────────────────────────────────────

	useEffect(() => {
		if (typeof window === "undefined") return;
		const update = () => {
			const byClass = document.documentElement.classList.contains("mantine-dark");
			const byAttr = document.documentElement.getAttribute("data-mantine-color-scheme") === "dark";
			const bySystem = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
			setIsDark(Boolean(byClass || byAttr || bySystem));
		};
		update();
		const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
		mq?.addEventListener?.("change", update);
		return () => mq?.removeEventListener?.("change", update);
	}, []);

	useEffect(() => {
		if (incomingCallId && !callType) currentCallIdRef.current = incomingCallId;
	}, [incomingCallId]);

	// ─── Audio ────────────────────────────────────────────────────────────────────

	const playAudio = () => {
		audioRef.current.loop = true;
		audioRef.current.play().catch(() => {});
	};

	const stopAudio = () => {
		audioRef.current.pause();
		audioRef.current.currentTime = 0;
	};

	// ─── Timer ────────────────────────────────────────────────────────────────────

	const startTimer = () => {
		callStartedAtRef.current = new Date();
		timerRef.current = setInterval(() => {
			setElapsed(Math.round((Date.now() - callStartedAtRef.current.getTime()) / 1000));
		}, 1000);
	};

	const stopTimer = () => {
		clearInterval(timerRef.current);
		timerRef.current = null;
		setElapsed(0);
	};

	// ─── Cleanup on unmount ───────────────────────────────────────────────────────

	useEffect(() => {
		return () => {
			try {
				if (typeof document !== "undefined") {
					if (document.fullscreenElement) void document.exitFullscreen();
					if (document.webkitFullscreenElement) document.webkitExitFullscreen?.();
				}
			} catch (_) {}
			stopAudio();
			stopTimer();
			stopAllStreams();
			currentCallRef.current?.close();
			closeDirectPeerConnection();
			destroyPeer();
		};
	}, []);

	const stopAllStreams = () => {
		streamRef.current?.getTracks().forEach((t) => t.stop());
		streamRef.current = null;
		blurRef.current?.stop();
		blurRef.current = null;
		screenStreamRef.current?.getTracks().forEach((t) => t.stop());
		screenStreamRef.current = null;
		remoteStreamRef.current = null;
	};

	// ─── Ringtone ─────────────────────────────────────────────────────────────────

	useEffect(() => {
		if (incomingCall) {
            playAudio();
            if (incomingCallId) socket?.emit("callDisplayed", { callId: incomingCallId });
        }
	}, [incomingCall, incomingCallId, socket]);

	// ─── Socket listeners ─────────────────────────────────────────────────────────

	useEffect(() => {
		if (!socket) return;

		const handleRemoteEndCall = (data = {}) => {
            if (data.callId && data.callId !== currentCallIdRef.current) return;
            endCall({ notifyRemote: false });
        };
        const handleCallRejected = handleRemoteEndCall;
        const handleUserBusy = handleRemoteEndCall;
		const handleIceCandidate = ({ candidate, from, callId }) => {
            if (callId && currentCallIdRef.current && callId !== currentCallIdRef.current) return;
			const peerId = partner?._id || caller;
			if (from && peerId && String(from) !== String(peerId)) return;
			void addDirectIceCandidate(candidate);
		};

        const handleConverged = async (data) => {
            if (currentCallIdRef.current !== data.replacedCallId || String(partner?._id) !== String(data.from)) return;
            setupGenerationRef.current++;
            currentCallIdRef.current = data.callId; // synchronous guard against duplicate deliveries
            socket.off('callAccepted', callAcceptedListenerRef.current);
            callAcceptedListenerRef.current = null;
            closeDirectPeerConnection();
            stopAudio();
            setIncomingCall(false);
            setCalling(false);
            try {
                const pc = createDirectPeerConnection(data.from, 'audio');
                await pc.setRemoteDescription(new RTCSessionDescription(data.signal));
                if (directPeerConnectionRef.current !== pc) return;
                await flushDirectIceCandidates();
                const idle = pc.getTransceivers().find(t => t.receiver?.track?.kind === 'video' && !t.sender.track);
                if (idle) idle.direction = 'sendrecv';
                const answer = await pc.createAnswer();
                if (directPeerConnectionRef.current !== pc) return;
                await pc.setLocalDescription(answer);
                if (directPeerConnectionRef.current !== pc) return;
                socket.emit('answerCall', { to: data.from, from: user._id, callId: data.callId,
                    signal: serializeSessionDescription(pc.localDescription || answer) });
                setCallAccepted(true);
                markCallConnected('audio');
            } catch (error) {
                if (currentCallIdRef.current === data.callId) endCall();
            }
        };
        socket.on('callConverged', handleConverged);

        const isCurrentRestart = (data) => data?.callId === currentCallIdRef.current && String(data.from) === String(partner?._id || caller);
        const handleRestartOffer = async (data) => {
            const pc = directPeerConnectionRef.current;
            if (!pc || !isCurrentRestart(data) || pc.signalingState !== 'stable' || data.signal?.type !== 'offer') return;
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(data.signal));
                if (directPeerConnectionRef.current !== pc) return;
                await flushDirectIceCandidates();
                const answer = await pc.createAnswer();
                if (directPeerConnectionRef.current !== pc) return;
                await pc.setLocalDescription(answer);
                if (directPeerConnectionRef.current !== pc) return;
                socket.emit('callRenegotiated', { callId: data.callId, restartId: data.restartId, signal: serializeSessionDescription(pc.localDescription) });
            } catch (error) { console.warn('Call recovery offer failed:', error.message); }
        };
        const handleRestartAnswer = async (data) => {
            const pc = directPeerConnectionRef.current;
            if (!pc || !isCurrentRestart(data) || data.restartId !== restartIdRef.current || pc.signalingState !== 'have-local-offer' || data.signal?.type !== 'answer') return;
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(data.signal));
                if (directPeerConnectionRef.current === pc) await flushDirectIceCandidates();
            } catch (error) { console.warn('Call recovery answer failed:', error.message); }
        };
        const handleRecoveryRequest = (data) => { if (isCurrentRestart(data)) void recoverDirectConnection(); };
        socket.on('callRenegotiate', handleRestartOffer);
        socket.on('callRenegotiated', handleRestartAnswer);
        socket.on('callRecoveryRequest', handleRecoveryRequest);

		// Dismiss incoming call on this device when the user answered on another device
		const handleAnsweredElsewhere = (data = {}) => {
            if (data.callId && data.callId !== currentCallIdRef.current) return;
			if (!incomingCall) return;
			stopAudio();
			setIncomingCall(false);
			if (setIncomingCallType) setIncomingCallType(null);
			if (setIncomingCallId) setIncomingCallId(null);
			setIncomingCallSignal?.(null);
		};

		socket.on("endCall", handleRemoteEndCall);
		socket.on("callRejected", handleCallRejected);
		socket.on("userBusy", handleUserBusy);
		socket.on("iceCandidate", handleIceCandidate);
		socket.on("callAnsweredElsewhere", handleAnsweredElsewhere);
		const handleScreenShare = ({ from, callId, sharing } = {}) => {
			const peerId = partner?._id || caller;
			if (from && peerId && String(from) !== String(peerId)) return;
			if (callId && currentCallIdRef.current && String(callId) !== String(currentCallIdRef.current)) return;
			setPeerSharing(Boolean(sharing));
		};
		const handleMediaState = ({ from, callId, cameraOn } = {}) => {
			if (!currentCallIdRef.current || String(callId) !== String(currentCallIdRef.current) || String(from) !== String(caller || partner?._id) || typeof cameraOn !== "boolean") return;
			setPeerCameraOn(cameraOn);
		};
		socket.on("callMediaState", handleMediaState);
		socket.on("callScreenShare", handleScreenShare);

		return () => {
            socket.off('callRenegotiate', handleRestartOffer);
            socket.off('callRenegotiated', handleRestartAnswer);
            socket.off('callRecoveryRequest', handleRecoveryRequest);
			socket.off("callScreenShare", handleScreenShare);
			socket.off("callMediaState", handleMediaState);
			socket.off("endCall", handleRemoteEndCall);
			socket.off("callRejected", handleCallRejected);
			socket.off("userBusy", handleUserBusy);
            socket.off("callConverged", handleConverged);
			socket.off("iceCandidate", handleIceCandidate);
			socket.off("callAnsweredElsewhere", handleAnsweredElsewhere);
		};
	}, [socket, incomingCall, partner?._id, caller]);

	// ─── Outgoing call trigger ────────────────────────────────────────────────────

	useEffect(() => {
		if (callType && !calling && !callAccepted) callUser();
	}, [callType]);

	// ─── PeerJS ───────────────────────────────────────────────────────────────────

	const buildPeerConfig = () => {
		let peerHost = "localhost";
		let isSecure = window?.location?.protocol === "https:";
		let apiPort = null;
		const rawApiUrl = `${config.api?.API_URL || ""}`.trim();
		if (rawApiUrl) {
			try {
				const normalized = rawApiUrl.match(/^https?:\/\//i)
					? rawApiUrl
					: rawApiUrl.startsWith("//")
						? `${window?.location?.protocol || "http:"}${rawApiUrl}`
						: `${window?.location?.protocol || "http://"}//${rawApiUrl}`;
				const apiUrl = new URL(normalized);
				peerHost = apiUrl.hostname || peerHost;
				isSecure = apiUrl.protocol === "https:";
				apiPort = apiUrl.port ? Number(apiUrl.port) : null;
			} catch (_) {}
		}
		const explicitPeerPortRaw = `${config.api?.PEERJS_PORT ?? ""}`.trim();
		const explicitPeerPort = /^\d+$/.test(explicitPeerPortRaw) ? Number(explicitPeerPortRaw) : null;
		const isLocalHost = ["localhost", "127.0.0.1", "::1"].includes(peerHost);
		const hasLegacyExternalPeerPort = !isLocalHost && isSecure && explicitPeerPort === 27605;
		const normalizedExplicitPeerPort = hasLegacyExternalPeerPort ? null : explicitPeerPort;
		const peerPort = normalizedExplicitPeerPort ?? apiPort ?? (isLocalHost ? 27605 : undefined);
		return { peerHost, isSecure, peerPort };
	};

	const initPeer = () =>
		new Promise((resolve, reject) => {
			if (peerRef.current && !peerRef.current.destroyed) {
				if (peerRef.current.id) resolve(peerRef.current);
				else {
					peerRef.current.once("open", () => resolve(peerRef.current));
					peerRef.current.once("error", reject);
				}
				return;
			}
			const { peerHost, isSecure, peerPort } = buildPeerConfig();
			const peerOptions = {
				host: peerHost,
				path: "/peerjs",
				secure: isSecure,
				debug: 2,
				key: "peerjs",
				config: {
					iceServers: [
						{ urls: "stun:stun.l.google.com:19302" },
						{ urls: "stun:stun1.l.google.com:19302" },
					],
				},
			};
			if (Number.isFinite(peerPort)) peerOptions.port = peerPort;
			const peer = new Peer(user._id, peerOptions);
			peerRef.current = peer;
			peer.once("open", () => resolve(peer));
			peer.once("error", reject);
		});

	const destroyPeer = () => {
		peerRef.current?.destroy();
		peerRef.current = null;
	};

	const detachIncomingPeerCallListener = useCallback(() => {
		if (!peerRef.current || !incomingPeerCallListenerRef.current) return;
		(peerRef.current.off ?? peerRef.current.removeListener)?.call(
			peerRef.current, "call", incomingPeerCallListenerRef.current
		);
		incomingPeerCallListenerRef.current = null;
	}, []);

	// ─── markCallConnected ────────────────────────────────────────────────────────

	const markCallConnected = useCallback(
		(resolvedCallType) => {
			if (!callStartedAtRef.current) startTimer();
			socket?.emit("callMediaState", { to: caller || partner?._id, callId: currentCallIdRef.current, cameraOn: streamRef.current?.getVideoTracks()[0]?.enabled ?? false });
			if (!connectedCallTypeRef.current) {
				connectedCallTypeRef.current = resolvedCallType || activeCallType || "audio";
			}
		},
		[activeCallType, socket, caller, partner?._id]
	);

	// ─── Call history message ─────────────────────────────────────────────────────

	const emitCallHistoryMessage = useCallback(
		(durationSeconds, endedAt, { isMissed = false } = {}) => {
			if (!socket || !user?._id || !partner?._id || callHistoryLoggedRef.current) return;
			const resolvedCallType = connectedCallTypeRef.current || activeCallType || "audio";
			const safeDuration = Math.max(0, Math.round(Number(durationSeconds) || 0));
			const callLabel = resolvedCallType === "video" ? "Video call" : "Audio call";
			let body;
			if (isMissed) body = `Missed ${callLabel.toLowerCase()}`;
			else if (safeDuration > 0) body = `${callLabel} • ${formatCallDuration(safeDuration)}`;
			else body = `${callLabel} (no answer)`;

			socket.emit("sendMessage", {
				newMessage: {
					from: user._id,
					to: [partner._id],
					...(chatId ? { chatId } : {}),
					body,
					readBy: [user._id],
					created: endedAt,
					call: {
						type: resolvedCallType,
						durationSeconds: safeDuration,
						startedAt: callStartedAtRef.current || endedAt,
						endedAt,
					},
				},
			});
			callHistoryLoggedRef.current = true;
		},
		[activeCallType, chatId, partner?._id, socket, user?._id]
	);

	// ─── CALLER SIDE ─────────────────────────────────────────────────────────────

	const callUser = async () => {
        const generation = ++setupGenerationRef.current;
		if (!partner?._id || !socket) { setCallType(null); return; }

		const isVideo = callType === "video";
		const mediaStream = await (async () => {
			try {
				return await navigator.mediaDevices.getUserMedia({
					video: isVideo ? getCameraVideoConstraints() : false,
					audio: audioEffects,
				});
			} catch (err) {
				console.error("getUserMedia error:", err);
				alert("Could not access camera/microphone.");
				return null;
			}
		})();

		if (generation !== setupGenerationRef.current) { mediaStream?.getTracks().forEach(track => track.stop()); return; }
        if (!mediaStream) { setCallType(null); return; }

		// Store stream and update state — local video element gets srcObject via callback ref
		assignLocalStream(mediaStream);
		setStream(mediaStream);

		currentCallIdRef.current = `${user._id}_${partner._id}_${Date.now()}`;
		setCalling(true);
		playAudio();

		let signalData = null;
		try {
			const pc = createDirectPeerConnection(partner._id, callType);
			// Audio calls still open an idle video channel so either side can share its screen
			// later with replaceTrack (no renegotiation), like video calls do.
			if (!isVideo) pc.addTransceiver("video", { direction: "sendrecv", streams: [mediaStream] });
			const offer = await pc.createOffer({
				offerToReceiveAudio: true,
				offerToReceiveVideo: isVideo,
			});
			if (generation !== setupGenerationRef.current) return;
            await pc.setLocalDescription(offer);
            if (generation !== setupGenerationRef.current) return;
			signalData = serializeSessionDescription(pc.localDescription || offer);
		} catch (err) {
			if (generation !== setupGenerationRef.current) return;
            console.error("Direct WebRTC offer failed:", err);
			closeDirectPeerConnection();
		}
		if (!signalData) {
			alert("Call service is unavailable. Please refresh the web app and try again.");
			setCalling(false);
			stopAudio();
			setCallType(null);
			streamRef.current?.getTracks().forEach((t) => t.stop());
			return;
		}

		const handleCallAccepted = async ({ from, callId, signal } = {}) => {
			if (from && String(from) !== String(partner._id)) return;
			if (callId && currentCallIdRef.current && String(callId) !== String(currentCallIdRef.current)) return;

			socket.off("callAccepted", handleCallAccepted);
			callAcceptedListenerRef.current = null;
			stopAudio();

			if (signal && directPeerConnectionRef.current) {
                const pc = directPeerConnectionRef.current;
				try {
					await pc.setRemoteDescription(new RTCSessionDescription(signal));
                    if (directPeerConnectionRef.current !== pc) return;
					await flushDirectIceCandidates();
                    if (directPeerConnectionRef.current !== pc) return;
					markCallConnected(callType);
					setCalling(false);
					setCallAccepted(true);
				} catch (err) {
					if (directPeerConnectionRef.current !== pc) return;
                    console.error("Failed to complete direct WebRTC call:", err);
					endCall({ notifyRemote: false });
				}
				return;
			}

			closeDirectPeerConnection();
			try {
				const peer = await initPeer();
				const call = peer.call(partner._id, streamRef.current, {
					metadata: { callType, callerName: user.name?.[lang] || "User" },
				});
				if (!call) throw new Error("peer.call() returned null");
				currentCallRef.current = call;

				call.on("stream", (remoteStream) => {
					// Store stream first, THEN transition state.
					// The callback ref on the remote <video> will assign srcObject when the
					// active-call modal mounts its new element.
					assignRemoteStream(remoteStream);
					markCallConnected(callType);
					// Transition: calling modal → active call modal
					setCalling(false);
					setCallAccepted(true);
				});

				call.on("close", () => endCall({ notifyRemote: false }));
				call.on("error", () => endCall());
			} catch (err) {
				console.error("Failed to connect peer call:", err);
				endCall({ notifyRemote: false });
			}
		};

		callAcceptedListenerRef.current = handleCallAccepted;
		socket.on("callAccepted", handleCallAccepted);

		socket.emit("callUser", {
			userToCall: partner._id,
			signalData,
			from: user._id,
			name: user.name?.[lang] || "User",
			callType,
			callId: currentCallIdRef.current,
			chatId,
		});
	};

	// ─── CALLEE SIDE ─────────────────────────────────────────────────────────────

	const answerCall = async () => {
		const isVideo = incomingCallType === "video";
		const mediaStream = await (async () => {
			try {
				return await navigator.mediaDevices.getUserMedia({
					video: isVideo ? getCameraVideoConstraints() : false,
					audio: audioEffects,
				});
			} catch (err) {
				console.error("getUserMedia error:", err);
				alert("Could not access camera/microphone.");
				return null;
			}
		})();

		if (!mediaStream) return;

		// Store and show local stream
		assignLocalStream(mediaStream);
		setStream(mediaStream);
		stopAudio();

		if (incomingCallSignal) {
			try {
				const pc = createDirectPeerConnection(caller, incomingCallType);
				await pc.setRemoteDescription(new RTCSessionDescription(incomingCallSignal));
				await flushDirectIceCandidates();
				if (!isVideo) {
					// Answer the caller's screen-share channel as sendrecv so we can share too.
					const idleVideo = pc.getTransceivers().find((t) => t.receiver?.track?.kind === "video" && !t.sender.track);
					if (idleVideo) {
						idleVideo.direction = "sendrecv";
						try { idleVideo.sender.setStreams?.(mediaStream); } catch (_) {}
					}
				}
				const answer = await pc.createAnswer();
				await pc.setLocalDescription(answer);

				if (socket) {
					socket.emit("answerCall", {
						to: caller,
						from: user._id,
						callId: currentCallIdRef.current,
						signal: serializeSessionDescription(pc.localDescription || answer),
					});
				}

				setIncomingCall(false);
				setIncomingCallSignal?.(null);
				setCallAccepted(true);
				markCallConnected(incomingCallType);
			} catch (err) {
				console.error("Failed to answer direct WebRTC call:", err);
				endCall({ notifyRemote: false });
			}
			return;
		}

		try {
			const peer = await initPeer();
			detachIncomingPeerCallListener();

			const handleIncomingPeerCall = (call) => {
				detachIncomingPeerCallListener();
				call.answer(streamRef.current);
				currentCallRef.current = call;

				call.on("stream", (remoteStream) => {
					// Same pattern: store first so callback ref can assign when element mounts
					assignRemoteStream(remoteStream);
					markCallConnected(incomingCallType);
				});

				call.on("close", () => endCall({ notifyRemote: false }));
				call.on("error", () => endCall());
			};

			incomingPeerCallListenerRef.current = handleIncomingPeerCall;
			peer.once("call", handleIncomingPeerCall);

			if (socket) {
				socket.emit("answerCall", { to: caller, from: user._id, callId: currentCallIdRef.current });
			}

			// Transition: incoming modal → active call modal
			setIncomingCall(false);
			setIncomingCallSignal?.(null);
			setCallAccepted(true);
		} catch (err) {
			console.error("Failed to initialize PeerJS for answering:", err);
			stopAudio();
			setIncomingCall(false);
			setIncomingCallType?.(null);
			setIncomingCallId?.(null);
			setIncomingCallSignal?.(null);
		}
	};

	// ─── SHARED ──────────────────────────────────────────────────────────────────

	const endCall = ({ notifyRemote = true, isMissed = false } = {}) => {
		if (isEndingCallRef.current) return;
		isEndingCallRef.current = true;

		try {
			if (typeof document !== "undefined") {
				if (document.fullscreenElement) void document.exitFullscreen();
				if (document.webkitFullscreenElement) document.webkitExitFullscreen?.();
			}
		} catch (_) {}
		setIsCallVideoFullscreen(false);
		setLocalPipAspect(DEFAULT_LOCAL_VIDEO_ASPECT);

		const endedAt = new Date();
		const durationSeconds = callStartedAtRef.current
			? Math.max(0, Math.round((endedAt.getTime() - callStartedAtRef.current.getTime()) / 1000))
			: 0;

		if (notifyRemote) {
			emitCallHistoryMessage(durationSeconds, endedAt, {
				isMissed: isMissed || (!callAccepted && durationSeconds === 0),
			});
		}

		// Clear video elements
		if (localVideoElRef.current) localVideoElRef.current.srcObject = null;
		if (remoteVideoElRef.current) remoteVideoElRef.current.srcObject = null;
		if (remoteAudioElRef.current) remoteAudioElRef.current.srcObject = null;

		if (screenStreamRef.current) {
			screenStreamRef.current.getTracks().forEach((t) => t.stop());
			screenStreamRef.current = null;
		}
		setIsSharing(false);
		setPeerSharing(false);
		setPeerCameraOn(true);
		setPeerVideoStalled(false);
		setFacingMode("user");
		setVideoMuted(false);
		blurRef.current?.stop();
		blurRef.current = null;
		setBackgroundBlur(false);
		peerVideoTrackRef.current = null;
		stopTimer();
		setupGenerationRef.current++;
        setCallEnded(true);
		setCallAccepted(false);
		setCalling(false);
		setIncomingCall(false);
		setCallType(null);
		setIsMinimized(false);
		setIncomingCallType?.(null);
		setIncomingCallId?.(null);
		setIncomingCallSignal?.(null);
		setChatId?.(null);
		stopAudio();

		if (callAcceptedListenerRef.current && socket) {
			socket.off("callAccepted", callAcceptedListenerRef.current);
			callAcceptedListenerRef.current = null;
		}
		detachIncomingPeerCallListener();
		stopAllStreams();
		setStream(null);

		currentCallRef.current?.close();
		currentCallRef.current = null;
		closeDirectPeerConnection();
		destroyPeer();

		if (socket && notifyRemote) {
			socket.emit("endCall", { to: caller || partner?._id, callId: currentCallIdRef.current });
		}

		const endedCallId = currentCallIdRef.current;
        setTimeout(() => {
            if (currentCallIdRef.current !== endedCallId) return;
			setCallEnded(false);
			callStartedAtRef.current = null;
			connectedCallTypeRef.current = null;
			currentCallIdRef.current = null;
			isEndingCallRef.current = false;
			callHistoryLoggedRef.current = false;
		}, 1000);
	};

	// ─── Controls ────────────────────────────────────────────────────────────────

	const updateAudioEffect = async (key, value) => {
		const stream = streamRef.current;
		const oldTrack = stream?.getAudioTracks()[0];
		if (!oldTrack || enhancementBusy) return;
		setEnhancementBusy(true);
		const next = { ...audioEffects, [key]: value };
		const deviceId = oldTrack.getSettings?.().deviceId;
		const openMic = async () =>
			(await navigator.mediaDevices.getUserMedia({ audio: { ...next, ...(deviceId ? { deviceId: { exact: deviceId } } : {}) } })).getAudioTracks()[0];
		let fresh;
		try {
			// Browsers fix echo cancellation / noise suppression when the mic opens; applyConstraints
			// on a live track is ignored or rejected. Reopen the mic and swap it into the call.
			fresh = await openMic();
			if (fresh.getSettings?.()[key] !== undefined && fresh.getSettings()[key] !== value) {
				// Some browsers share one processing chain per device: release the old capture first.
				fresh.stop();
				oldTrack.stop();
				fresh = await openMic();
			}
			fresh.enabled = !audioMuted;
			const pc = directPeerConnectionRef.current || currentCallRef.current?.peerConnection;
			const sender = pc?.getSenders?.().find((s) => s.track === oldTrack || s.track?.kind === "audio");
			if (sender) await sender.replaceTrack(fresh);
			stream.removeTrack(oldTrack);
			stream.addTrack(fresh);
			oldTrack.stop();
			setAudioEffects(next);
		} catch (error) {
			if (fresh && fresh !== stream.getAudioTracks()[0]) fresh.stop();
			alert(`Could not change the microphone setting: ${error.message}`);
		} finally {
			setEnhancementBusy(false);
		}
	};

	/** Phones: front ↔ back. Laptops with several webcams: the next one. */
	const flipCamera = async () => {
		const source = streamRef.current;
		const oldTrack = source?.getVideoTracks()[0];
		if (!oldTrack || enhancementBusy || isSharing) return;
		setEnhancementBusy(true);
		const settings = oldTrack.getSettings?.() || {};
		const nextFacing = (settings.facingMode || facingMode) === "environment" ? "user" : "environment";
		let videoConstraints = { facingMode: { exact: nextFacing } };
		let fresh;
		let effect;
		try {
			if (!settings.facingMode) {
				const cameras = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
				const index = cameras.findIndex((d) => d.deviceId === settings.deviceId);
				const nextCamera = cameras[(index + 1) % cameras.length];
				if (nextCamera?.deviceId) videoConstraints = { deviceId: { exact: nextCamera.deviceId } };
			}
			// Most phones cannot open both cameras at once: release the current one first.
			oldTrack.stop();
			fresh = (await navigator.mediaDevices.getUserMedia({ video: videoConstraints })).getVideoTracks()[0];
			fresh.enabled = oldTrack.enabled;
			source.removeTrack(oldTrack);
			source.addTrack(fresh);
			if (blurRef.current) effect = await createBackgroundBlur(source);
			const output = effect?.stream || source;
			output.getVideoTracks()[0].enabled = fresh.enabled;
			await replaceOutgoingVideoTrack(output.getVideoTracks()[0]);
			blurRef.current?.stop();
			blurRef.current = effect || null;
			if (localVideoElRef.current) localVideoElRef.current.srcObject = output;
			setFacingMode(fresh.getSettings?.().facingMode || (videoConstraints.facingMode ? nextFacing : facingMode));
		} catch (error) {
			effect?.stop();
			// Get a camera back so the call does not silently lose video.
			if (!source.getVideoTracks().some((t) => t.readyState === "live")) {
				try {
					const restored = (await navigator.mediaDevices.getUserMedia({ video: getCameraVideoConstraints() })).getVideoTracks()[0];
					source.getVideoTracks().forEach((t) => source.removeTrack(t));
					source.addTrack(restored);
					blurRef.current?.stop();
					blurRef.current = null;
					setBackgroundBlur(false);
					await replaceOutgoingVideoTrack(restored);
					if (localVideoElRef.current) localVideoElRef.current.srcObject = source;
					setFacingMode("user");
				} catch (_) {}
			}
			alert(`Could not switch the camera: ${error.message}`);
		} finally {
			setEnhancementBusy(false);
		}
	};

	const toggleBackgroundBlur = async (enabled) => {
		if (enhancementBusy || isSharing) return;
		const source = streamRef.current;
		if (!source?.getVideoTracks()[0]) return;
		setEnhancementBusy(true);
		let effect;
		try {
			if (enabled) effect = await createBackgroundBlur(source);
			if (source !== streamRef.current) { effect?.stop(); return; }
			const output = effect?.stream || source;
			output.getVideoTracks()[0].enabled = source.getVideoTracks()[0].enabled;
			if (!await replaceOutgoingVideoTrack(output.getVideoTracks()[0])) throw new Error("Connect the call before changing background blur.");
			blurRef.current?.stop();
			blurRef.current = effect || null;
			if (localVideoElRef.current) localVideoElRef.current.srcObject = output;
			setBackgroundBlur(enabled);
		} catch (error) { effect?.stop(); alert(error.message); }
		finally { setEnhancementBusy(false); }
	};

	const toggleMute = () => {
		const track = streamRef.current?.getAudioTracks()[0];
		if (!track) return;
		track.enabled = !track.enabled;
		setAudioMuted(!track.enabled);
	};

	const toggleVideo = () => {
		if (activeCallType !== "video") return;
		const track = streamRef.current?.getVideoTracks()[0];
		if (!track) return;
		track.enabled = !track.enabled;
		setVideoMuted(!track.enabled);
		if (blurRef.current) blurRef.current.stream.getVideoTracks()[0].enabled = track.enabled;
		socket?.emit("callMediaState", { to: caller || partner?._id, callId: currentCallIdRef.current, cameraOn: track.enabled });
	};

	const stopScreenShare = useCallback(async ({ stopTracks = true } = {}) => {
		const screenStream = screenStreamRef.current;
		screenStreamRef.current = null;
		if (stopTracks) screenStream?.getTracks().forEach((t) => t.stop());

		setIsSharing(false);
		emitScreenShareState(false);

		// Video call: back to the camera. Audio call: the video channel goes idle again.
		const camTrack = (blurRef.current?.stream || streamRef.current)?.getVideoTracks()[0] || null;
		try {
			await replaceOutgoingVideoTrack(camTrack);
		} catch (err) {
			console.error("Failed to restore camera after screen share:", err);
		}
		if (localVideoElRef.current) localVideoElRef.current.srcObject = blurRef.current?.stream || streamRef.current;
	}, [replaceOutgoingVideoTrack, emitScreenShareState]);

	const startScreenShare = useCallback(async () => {
		if (!navigator.mediaDevices?.getDisplayMedia) {
			alert("Screen sharing is available only in supported browsers over HTTPS or localhost.");
			return;
		}

		try {
			const screenStream = await navigator.mediaDevices.getDisplayMedia({
				video: { cursor: "always" },
				audio: false,
			});
			const videoTrack = screenStream.getVideoTracks()[0];
			if (!videoTrack) {
				screenStream.getTracks().forEach((t) => t.stop());
				return;
			}

			const replaced = await replaceOutgoingVideoTrack(videoTrack);
			if (!replaced) {
				screenStream.getTracks().forEach((t) => t.stop());
				alert("Screen sharing can start only after the call is connected. If it is, the other side's app is too old to receive a shared screen in this call.");
				return;
			}

			blurRef.current?.stop();
		blurRef.current = null;
		screenStreamRef.current?.getTracks().forEach((t) => t.stop());
			screenStreamRef.current = screenStream;
			setIsSharing(true);
			emitScreenShareState(true);
			if (localVideoElRef.current) localVideoElRef.current.srcObject = screenStream;

			videoTrack.addEventListener("ended", () => {
				stopScreenShare({ stopTracks: false });
			}, { once: true });
		} catch (err) {
			if (err.name !== "NotAllowedError") console.error("Screen share error:", err);
		}
	}, [replaceOutgoingVideoTrack, stopScreenShare, emitScreenShareState]);

	const toggleScreenShare = () => {
		if (isSharing) stopScreenShare();
		else startScreenShare();
	};

	// ─── Minimized widget ─────────────────────────────────────────────────────────

	if (isMinimized && callAccepted) {
		return (
			<Paper
				shadow="xl"
				radius="md"
				p="xs"
				style={{
					position: "fixed",
					bottom: 24,
					right: 24,
					zIndex: 9999,
					backgroundColor: isDark ? "#1a1a2e" : "#1f2937",
					color: "#fff",
					cursor: "pointer",
					minWidth: 200,
				}}
				onClick={() => setIsMinimized(false)}
			>
				<Flex align="center" gap="xs">
					<Avatar size={28} radius="xl" src={getCallPartnerImage(partner, 32)} />
					<Text size="sm" color="white">{getCallPartnerName(partner, lang, "Call")}</Text>
					<Badge size="xs" color="blue">{formatCallDuration(elapsed)}</Badge>
					<ActionIcon
						size="sm"
						color="red"
						variant="filled"
						onClick={(e) => { e.stopPropagation(); endCall(); }}
					>
						<IconPhoneOff size={12} />
					</ActionIcon>
				</Flex>
			</Paper>
		);
	}

	// ─── JSX ──────────────────────────────────────────────────────────────────────

	return (
		<>
			{/* Outgoing call — shows local preview while ringing */}
			<Modal
				opened={calling && !callAccepted}
				onClose={endCall}
				overlayProps={{ opacity: isDark ? 0.7 : 0.55, blur: 3 }}
				styles={callingModalStyles}
				centered
				withCloseButton={false}
				size="sm"
			>
				<Stack align="center" spacing="sm">
					{callType === "video" && (
						<Box
							mx="auto"
							w="100%"
							style={{
								maxWidth: 280,
								maxHeight: 260,
								aspectRatio: localPipAspect,
								borderRadius: 16,
								overflow: "hidden",
								backgroundColor: "#000",
							}}
						>
							<video
								ref={setLocalVideoRef}
								autoPlay
								muted
								playsInline
								onLoadedMetadata={(e) => updateLocalPipFromVideoElement(e.currentTarget)}
								style={{
									width: "100%",
									height: "100%",
									objectFit: "cover",
									display: "block",
										transform: !isSharing && mirrorSelf && facingMode !== "environment" ? "scaleX(-1)" : "none",
										visibility: videoMuted && !isSharing ? "hidden" : "visible",
								}}
							/>
						</Box>
					)}
					<Avatar
						size={88}
						radius="xl"
						src={getCallPartnerImage(partner, 120)}
					/>
					<Title order={2} align="center" style={{ lineHeight: 1.1 }}>
						{getCallPartnerName(partner, lang)}
					</Title>
					<Text size="sm" color="dimmed">
						{callType === "video" ? "Video calling..." : "Calling..."}
					</Text>
					<Loader size="lg" />
					<Button
						onClick={endCall}
						size="md"
						color="red"
						leftIcon={<IconPhoneOff size={18} />}
					>
						Cancel
					</Button>
				</Stack>
			</Modal>

			{/* Incoming call */}
			<Modal
				opened={incomingCall && !callAccepted && canViewCurrentIncomingCall}
				onClose={() => {
					stopAudio();
					setIncomingCall(false);
					setIncomingCallType?.(null);
					setIncomingCallId?.(null);
					setIncomingCallSignal?.(null);
				}}
				overlayProps={{ opacity: isDark ? 0.7 : 0.55, blur: 3 }}
				styles={callingModalStyles}
				centered
				withCloseButton={false}
				size="sm"
			>
				<Stack align="center" spacing="sm">
					<Avatar
						size={88}
						radius="xl"
						src={getCallPartnerImage(partner, 120)}
					/>
					<Title order={2} align="center" style={{ lineHeight: 1.1 }}>
						{getCallPartnerName(partner, lang)}
					</Title>
					<Text size="sm" color="dimmed">
						Incoming {incomingCallType === "video" ? "video " : ""}call...
					</Text>
					<Group position="center" mt="xs" spacing="sm">
						<Button
							onClick={answerCall}
							size="md"
							leftIcon={<IconPhone size={18} />}
							disabled={!canAnswerCurrentIncomingCall}
						>
							Answer
						</Button>
						<Button
							onClick={() => {
								socket?.emit("rejectCall", { to: caller || partner?._id, callId: currentCallIdRef.current || incomingCallId });
								endCall({ notifyRemote: false });
							}}
							size="md"
							color="red"
							leftIcon={<IconPhoneOff size={18} />}
						>
							Decline
						</Button>
					</Group>
				</Stack>
			</Modal>

			{/* Active call */}
			<Modal
				opened={callAccepted && !callEnded && !isMinimized}
				onClose={endCall}
				size={activeCallType === "video" || peerSharing ? "xl" : "md"}
				overlayProps={{ opacity: isDark ? 0.7 : 0.55, blur: 3 }}
				styles={modalStyles}
				centered
				withCloseButton={false}
				fullScreen={activeCallType === "video"}
			>
				<Stack
					align="center"
					spacing="xl"
					p={activeCallType === "video" ? "md" : "xl"}
					style={activeCallType === "video" ? { position: "relative" } : undefined}
				>
					{/* Minimize only for audio: video uses the matching circle button in the bottom bar to avoid extra header height / scroll on small viewports */}
					{activeCallType !== "video" && (
						<Flex w="100%" justify="flex-end">
							<Tooltip label="Minimize">
								<ActionIcon variant="subtle" onClick={() => setIsMinimized(true)}>
									<IconMinimize size={18} />
								</ActionIcon>
							</Tooltip>
						</Flex>
					)}

					{activeCallType === "video" ? (
						<Box ref={videoCallStageRef} w="100%" pos="relative" style={{ display: "flex", flexDirection: "column" }}>
							{/* Remote video — full screen */}
							<video
								ref={setRemoteVideoRef}
								autoPlay
								playsInline
								style={{
									width: "100%",
									height: "calc(100dvh - 168px)",
									backgroundColor: "#000",
									borderRadius: "8px",
									objectFit: peerSharing ? "contain" : "cover",
								}}
							/>
							{(!peerCameraOn || peerVideoStalled) && !peerSharing && (
								<Box style={{ position: "absolute", inset: 0, background: "#000", borderRadius: 8, display: "grid", placeContent: "center", color: "white", textAlign: "center", padding: 16 }}>
									<IconVideoOff size={40} style={{ margin: "0 auto 12px" }} />
									<Text size="lg" weight={600}>
										{peerCameraOn
											? `${getCallPartnerName(partner, lang)}'s video is paused`
											: `${getCallPartnerName(partner, lang)} turned off the camera`}
									</Text>
								</Box>
							)}
							{/* Local video — PiP: box matches real camera aspect (e.g. 9:16 on phone) */}
							<Box
								style={{
									position: "absolute",
									bottom: 12,
									right: 12,
									maxWidth: 220,
									maxHeight: "min(50dvh, 420px)",
									width: "min(200px, 42vw)",
									aspectRatio: localPipAspect,
									borderRadius: 18,
									overflow: "hidden",
									border: isDark ? "2px solid #374151" : "2px solid white",
									backgroundColor: "#000",
									// Safari leaks the <video> past rounded corners without its own layer.
									isolation: "isolate",
								}}
							>
								<video
									ref={setLocalVideoRef}
									autoPlay
									muted
									playsInline
									onLoadedMetadata={(e) => updateLocalPipFromVideoElement(e.currentTarget)}
									style={{
										width: "100%",
										height: "100%",
										objectFit: "cover",
										display: "block",
										transform: !isSharing && mirrorSelf && facingMode !== "environment" ? "scaleX(-1)" : "none",
										visibility: videoMuted && !isSharing ? "hidden" : "visible",
									}}
								/>
								{canFlipCamera && !videoMuted && !isSharing && (
									<Tooltip label="Flip camera">
										<ActionIcon
											aria-label="Flip camera"
											radius="xl"
											size="lg"
											variant="filled"
											disabled={enhancementBusy}
											onClick={flipCamera}
											style={{ position: "absolute", top: 8, right: 8, backgroundColor: "rgba(0,0,0,0.5)", color: "#fff" }}
										>
											<IconCameraRotate size={18} />
										</ActionIcon>
									</Tooltip>
								)}
							</Box>
						</Box>
					) : (
						<>
							<audio ref={setRemoteAudioRef} autoPlay playsInline />
							{peerSharing && peerVideoTrackRef.current ? (
								<video
									ref={setPeerScreenRef}
									autoPlay
									playsInline
									muted
									style={{ width: "100%", maxHeight: "65dvh", backgroundColor: "#000", borderRadius: 8, objectFit: "contain" }}
								/>
							) : (
								<Avatar size={120} radius="xl" src={getCallPartnerImage(partner, 120)} />
							)}
							<div>
								<Title order={2} align="center">{getCallPartnerName(partner, lang)}</Title>
								<Text size="sm" color="dimmed" align="center">{formatCallDuration(elapsed)}</Text>
								{peerSharing && (
									<Text size="xs" color="dimmed" align="center">Sharing their screen</Text>
								)}
								{isSharing && (
									<Text size="xs" color="red" align="center">You are sharing your screen</Text>
								)}
							</div>
						</>
					)}

					{/* Timer overlay for video mode (below modal chrome; no top control row) */}
					{activeCallType === "video" && (
						<Badge
							color="blue"
							size="lg"
							style={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)" }}
						>
							{formatCallDuration(elapsed)}
						</Badge>
					)}

					<Flex gap="md" mt="lg">
						<Tooltip label={audioMuted ? "Unmute" : "Mute"}>
							<Button size="lg" radius="xl" color={audioMuted ? "red" : "blue"} onClick={toggleMute} w={60} h={60} p={0}>
								{audioMuted
									? <IconMicrophoneOff style={{ width: rem(24), height: rem(24) }} />
									: <IconMicrophone style={{ width: rem(24), height: rem(24) }} />}
							</Button>
						</Tooltip>

						{activeCallType === "video" && (
							<Tooltip label={videoMuted ? "Enable camera" : "Disable camera"}>
								<Button size="lg" radius="xl" color={videoMuted ? "red" : "blue"} onClick={toggleVideo} w={60} h={60} p={0}>
									{videoMuted
										? <IconVideoOff style={{ width: rem(24), height: rem(24) }} />
										: <IconVideo style={{ width: rem(24), height: rem(24) }} />}
								</Button>
							</Tooltip>
						)}

						<Popover position="top" withinPortal>
							<Popover.Target><Button radius="xl">Enhancements</Button></Popover.Target>
							<Popover.Dropdown><Stack spacing="sm">
								<Switch label="Noise suppression" checked={audioEffects.noiseSuppression} disabled={enhancementBusy} onChange={e => updateAudioEffect("noiseSuppression", e.currentTarget.checked)} />
								<Switch label="Echo cancellation" checked={audioEffects.echoCancellation} disabled={enhancementBusy} onChange={e => updateAudioEffect("echoCancellation", e.currentTarget.checked)} />
								{activeCallType === "video" && <>
									<Switch label="Mirror my preview" checked={mirrorSelf} onChange={e => setMirrorSelf(e.currentTarget.checked)} />
									<Switch label="Background blur" checked={backgroundBlur} disabled={enhancementBusy || isSharing} onChange={e => toggleBackgroundBlur(e.currentTarget.checked)} />
								</>}
							</Stack></Popover.Dropdown>
						</Popover>
						<Tooltip label={isSharing ? "Stop sharing" : "Share screen"}>
							<Button size="lg" radius="xl" color={isSharing ? "green" : "blue"} onClick={toggleScreenShare} w={60} h={60} p={0}>
								{isSharing
									? <IconScreenShareOff style={{ width: rem(24), height: rem(24) }} />
									: <IconScreenShare style={{ width: rem(24), height: rem(24) }} />}
							</Button>
						</Tooltip>

						{activeCallType === "video" && (
							<Tooltip label={isCallVideoFullscreen ? "Exit full screen" : "Full screen"}>
								<Button size="lg" radius="xl" color="blue" onClick={toggleCallVideoFullscreen} w={60} h={60} p={0}>
									{isCallVideoFullscreen
										? <IconArrowsMinimize style={{ width: rem(24), height: rem(24) }} />
										: <IconArrowsMaximize style={{ width: rem(24), height: rem(24) }} />}
								</Button>
							</Tooltip>
						)}

						{activeCallType === "video" && (
							<Tooltip label="Minimize">
								<Button size="lg" radius="xl" color="blue" onClick={() => setIsMinimized(true)} w={60} h={60} p={0}>
									<IconMinimize style={{ width: rem(24), height: rem(24) }} />
								</Button>
							</Tooltip>
						)}

						<Tooltip label="End call">
							<Button size="lg" radius="xl" color="red" onClick={endCall} w={60} h={60} p={0}>
								<IconPhoneOff style={{ width: rem(24), height: rem(24) }} />
							</Button>
						</Tooltip>
					</Flex>
				</Stack>
			</Modal>

			</>
		);
	}
